import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { ListPagination } from "@/components/ListPagination";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button as UiButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileCheck2, ExternalLink, Printer, FileDown, Trash2, Search, MessageCircle, Camera, Copy } from "lucide-react";
import { inr, fmtDate, today } from "@/lib/format";
import { printPodRegister } from "@/lib/print";
import { toast } from "sonner";

export default function PODUpload() {
  const [rows, setRows] = useState(null);
  const [uploadingId, setUploadingId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalCust, setPortalCust] = useState("");
  const [portalLink, setPortalLink] = useState("");
  const [portalMeta, setPortalMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [branchPick, setBranchPick] = useState("All");
  const [chaseAllIdx, setChaseAllIdx] = useState(0);
  const [customersList, setCustomersList] = useState([]);
  const [portals, setPortals] = useState([]);
  const loadPortals = () => api.get("/pod-portals").then((r) => setPortals(r.data || [])).catch(() => {});
  const [staffList, setStaffList] = useState([]);
  const [branchContacts, setBranchContacts] = useState({});
  useEffect(() => {
    api.get("/staff-master").then((r) => setStaffList(r.data || [])).catch(() => {});
    api.get("/branch-contacts").then((r) => setBranchContacts(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomersList(r.data || [])).catch(() => {});
    loadPortals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const codeByName = (() => {
    const m = {};
    customersList.forEach((c) => { if (c.name && c.customer_code) m[String(c.name).trim().toLowerCase()] = c.customer_code; });
    return m;
  })();
  const custCode = (r) => codeByName[String(r.consignor || r.customer_name || "").trim().toLowerCase()] || "";
  const podWa = async (r) => {
    const name = String(r.consignor || r.customer_name || "").trim();
    const cust = customersList.find((c) => String(c.name || "").trim().toLowerCase() === name.toLowerCase());
    const lr = String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "");
    let link = "";
    try {
      const res = await api.post("/pod-share", { shipment_id: r.id });
      link = `${API}/public/pod/${res.data.id}`;
    } catch {}
    const msg = [
      "*NTC — Delivery Confirmation (POD)*",
      `LR No.: ${lr}`,
      `Route: ${r.origin || "—"} → ${r.destination || "—"}`,
      ...(r.consignee ? [`Consignee: ${r.consignee}`] : []),
      ...(r.invoice_no ? [`Invoice No.: ${r.invoice_no}`] : []),
      "",
      "Your consignment has been DELIVERED and the signed POD has been uploaded to our records.",
      ...(link ? [`View the signed POD: ${link}`, "(link valid for 30 days)"] : ["A copy of the signed POD is available on request."]),
      "— Nisha Transport Corporation",
    ].join("\n");
    const digits = String(cust?.phone || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const fileInputs = useRef({});
  const cameraInputs = useRef({});
  const token = localStorage.getItem("ntc_token");

  const load = async () => {
    try {
      const res = await api.get("/shipments");
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const pick = (id) => fileInputs.current[id]?.click();

  const onFile = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/consignments/${id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = rows.find((x) => x.id === id);
      toast.success("POD uploaded — consignment marked Delivered", {
        description: r ? `Send the delivery confirmation to ${r.consignor || r.customer_name || "the customer"}'s WhatsApp.` : undefined,
        duration: 12000,
        action: r ? { label: "WhatsApp Customer", onClick: () => podWa(r) } : undefined,
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingId("");
      if (fileInputs.current[id]) fileInputs.current[id].value = "";
    }
  };

  const podUrl = (r) => `${API}/files/${r.pod_path}?auth=${token}`;

  const deletePod = async (r) => {
    if (!window.confirm(`Remove the uploaded POD for ${r.lr_no || r.consignment_no || "this consignment"}?`)) return;
    try {
      await api.delete(`/consignments/${r.id}/pod`);
      toast.success("POD removed — you can upload the correct one");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inRange = (r) => {
    const d = (r.lr_date || r.dispatch_date || "").slice(0, 10);
    if (fromDate && (!d || d < fromDate)) return false;
    if (toDate && (!d || d > toDate)) return false;
    if (pendingOnly && r.pod_path) return false;
    if (branchPick !== "All" && (r.office || r.office_name || "—") !== branchPick) return false;
    const needle = q.trim().toLowerCase();
    if (needle) {
      const hay = [r.consignor, r.customer_name, custCode(r), String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""), r.origin, r.destination, r.consignee, r.vehicle_no, r.vendor_name];
      if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
    }
    return true;
  };
  const filtered = (rows || []).filter(inRange);
  const podPages = Math.max(1, Math.ceil(filtered.length / 20));
  const curPodPage = Math.min(page, podPages);
  const podOffset = (curPodPage - 1) * 20;
  const pagedPods = filtered.slice(podOffset, podOffset + 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [fromDate, toDate, pendingOnly, q, branchPick]);
  const allSel = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((p) => { const n = new Set(p); if (allSel) filtered.forEach((r) => n.delete(r.id)); else filtered.forEach((r) => n.add(r.id)); return n; });
  const selectedRows = filtered.filter((r) => selected.has(r.id));
  const printList = () => printPodRegister(selectedRows.length ? selectedRows : filtered);

  const exportCsv = () => {
    const list = selectedRows.length ? selectedRows : filtered;
    const headers = ["SL No.", "Branch", "LR No.", "LR Date", "From", "Destination", "Consignor", "Customer Code", "Consignee", "PKG", "Basis", "Invoice No.", "Invoice Date", "Invoice Value", "Vehicle No.", "Vehicle Type", "Freight", "Vendor/Broker Name", "Delivery Date", "POD Status"];
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.join(",")];
    list.forEach((r, i) => {
      lines.push([
        i + 1,
        r.office || r.office_name || "",
        String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""),
        r.lr_date || r.dispatch_date || "",
        r.origin || "",
        r.destination || "",
        r.consignor || "",
        custCode(r) || "",
        r.consignee || "",
        r.packages || "",
        r.basis || "",
        r.invoice_no || "",
        r.invoice_date || "",
        r.invoice_value || "",
        r.vehicle_no || "",
        r.vehicle_type || "",
        r.freight_amount || 0,
        r.vendor_name || "",
        r.delivery_date || "",
        r.pod_path ? "Uploaded" : "Pending",
      ].map(cell).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pod-status-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div>
      <PageHeader
        title="POD Upload"
        subtitle="Attach Proof of Delivery (image/PDF) to a consignment. Uploading marks it as Delivered."
        action={
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input
                  className="h-9 w-[190px] pl-8 pr-7"
                  placeholder="Customer / code / LR"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="pod-search"
                />
                {q && (
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="pod-search-clear">
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-slate-50 px-3 py-2" data-testid="pod-pending-toggle">
              <Switch checked={pendingOnly} onCheckedChange={setPendingOnly} data-testid="pod-pending-only" />
              <Label className="text-xs font-medium text-slate-600">Pending only</Label>
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-[150px]" data-testid="pod-from-date" />
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-[150px]" data-testid="pod-to-date" />
            </div>
            {(fromDate || toDate) && (
              <UiButton variant="outline" size="sm" className="h-9" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="pod-clear-dates">Clear</UiButton>
            )}
            <UiButton variant="outline" size="sm" className="h-9" onClick={exportCsv} disabled={!filtered.length} data-testid="pod-excel-btn">
              <FileDown className="mr-2 h-4 w-4" /> Excel
            </UiButton>
            <UiButton variant="outline" size="sm" className="h-9 border-sky-300 text-sky-700 hover:bg-sky-50" onClick={() => setPortalOpen(true)} data-testid="pod-portal-btn">
              <ExternalLink className="mr-2 h-4 w-4" /> POD Portal
            </UiButton>
            <UiButton size="sm" className="h-9" onClick={printList} disabled={!filtered.length} data-testid="pod-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{selectedRows.length ? ` (${selectedRows.length})` : ""}
            </UiButton>
          </div>
        }
      />

      {rows !== null && (() => {
        const pend = (rows || []).filter((r) => !r.pod_path);
        if (!pend.length) return null;
        return (
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="pod-pending-totals">
            <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700" data-testid="pod-pending-freight-total">
              Pending POD freight: {inr(pend.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0))} ({pend.length} LRs)
            </span>
            <span className="rounded-full border border-sky-300 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700" data-testid="pod-pending-invoice-total">
              Pending POD invoice value: {inr(pend.reduce((s, r) => s + (Number(r.invoice_value) || 0), 0))}
            </span>
          </div>
        );
      })()}

      {rows !== null && (() => {
        const today = Date.now();
        const aging = (rows || [])
          .filter((r) => !r.pod_path)
          .map((r) => {
            const d = String(r.delivery_date || r.delivered_at || "").slice(0, 10);
            if (!d) return null;
            const days = Math.floor((today - new Date(d).getTime()) / 86400000);
            return days > 7 ? { r, days } : null;
          })
          .filter(Boolean)
          .sort((a, b) => b.days - a.days);
        if (!aging.length) return null;
        return (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50/70 px-4 py-3" data-testid="pod-aging-card">
            <div className="text-sm font-bold text-red-800">POD overdue — {aging.length} consignment{aging.length > 1 ? "s" : ""} delivered over 7 days ago with NO POD</div>
            <div className="mt-1.5 space-y-1">
              {aging.slice(0, 8).map(({ r, days }) => (
                <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-red-800" data-testid="pod-aging-row">
                  <span className="font-bold">LR {String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "")}</span>
                  <span>· {r.customer_name || r.consignor || "—"}</span>
                  <span>· {r.office || r.office_name || "—"}</span>
                  <span>· delivered {String(r.delivery_date || r.delivered_at || "").slice(0, 10)}</span>
                  <span className="ml-auto rounded-full bg-red-600 px-2 py-0.5 font-bold text-white">{days} days</span>
                </div>
              ))}
              {aging.length > 8 && <div className="text-xs text-red-700">+{aging.length - 8} more…</div>}
            </div>
            {(() => {
              const byB = {};
              aging.forEach(({ r, days }) => {
                const b = r.office || r.office_name || "—";
                (byB[b] = byB[b] || []).push({ r, days });
              });
              return (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-bold text-red-700">Chase on WhatsApp:</span>
                  {Object.keys(byB).sort().map((b) => (
                    <button
                      key={b}
                      type="button"
                      className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 hover:bg-red-100"
                      onClick={() => {
                        const list = byB[b];
                        const staff = staffList.find((s) => (s.branch || "") === b && String(s.phone || "").replace(/\D/g, "").length >= 10);
                        const digits = String(branchContacts[b] || "").replace(/\D/g, "") || String(staff?.phone || "").replace(/\D/g, "");
                        const lines = list.slice(0, 25).map(({ r, days }, i) => `${i + 1}. LR ${String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "")} — ${r.customer_name || r.consignor || "—"} — delivered ${String(r.delivery_date || r.delivered_at || "").slice(0, 10)} (*${days} days ago*)`);
                        const msg = `*NTC — POD OVERDUE 7+ DAYS (${b.toUpperCase()} BRANCH)*\n\n${lines.join("\n")}${list.length > 25 ? `\n…and ${list.length - 25} more` : ""}\n\n*${list.length} consignment(s) delivered over a week ago still have NO POD.* Upload the signed PODs TODAY.`;
                        if (!digits) toast.info(`No WhatsApp number for ${b} — opening WhatsApp without a number.`);
                        window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
                      }}
                      data-testid={`pod-aging-chase-${String(b).toLowerCase()}`}
                    >
                      <MessageCircle className="h-3 w-3" /> {b} ({byB[b].length})
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        );
      })()}

      {portals.filter((p) => !p.last_visited_at).length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3" data-testid="portal-unviewed-card">
          <div className="text-sm font-bold text-amber-800">
            Portal never opened — {portals.filter((p) => !p.last_visited_at).length} customer{portals.filter((p) => !p.last_visited_at).length > 1 ? "s" : ""} never viewed their POD portal link
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {portals.filter((p) => !p.last_visited_at).map((p) => {
              const link = `${API}/public/pod-portal/${p.id}`;
              const slug = String(p.customer_name).toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-bold text-amber-800" data-testid={`portal-unviewed-${slug}`}>
                  {p.customer_name}
                  <span className="font-normal text-amber-600">· since {fmtDate(String(p.created_at).slice(0, 10))}</span>
                  <button
                    type="button"
                    className="ml-1 text-emerald-600 hover:text-emerald-800"
                    title="Re-send portal link on WhatsApp"
                    data-testid={`portal-unviewed-wa-${slug}`}
                    onClick={() => {
                      const cust = customersList.find((c) => String(c.name || "").trim().toLowerCase() === String(p.customer_name).trim().toLowerCase());
                      const digits = String(cust?.phone || "").replace(/\D/g, "");
                      const msg = [
                        "*NTC — Your POD Portal (reminder)*",
                        `Dear ${p.customer_name},`,
                        "You can view ALL your delivered consignments and signed PODs anytime at:",
                        link,
                        "",
                        "Bookmark this link — it updates automatically as new deliveries complete.",
                        "— Nisha Transport Corporation",
                      ].join("\n");
                      window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
                    }}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    className="text-slate-500 hover:text-slate-800"
                    title="Copy portal link"
                    data-testid={`portal-unviewed-copy-${slug}`}
                    onClick={() => { navigator.clipboard?.writeText(link); toast.success("Portal link copied"); }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {rows !== null && (() => {
        const byBranch = {};
        (rows || []).forEach((r) => {
          if (r.pod_path) return;
          const b = r.office || r.office_name || "—";
          if (!byBranch[b]) byBranch[b] = [];
          byBranch[b].push(r);
        });
        const names = Object.keys(byBranch).sort();
        if (names.length < 2) return null;
        const chaseBranch = (b) => {
          const list = byBranch[b];
          const staff = staffList.find((s) => (s.branch || "") === b && String(s.phone || "").replace(/\D/g, "").length >= 10);
          const digits = String(branchContacts[b] || "").replace(/\D/g, "") || String(staff?.phone || "").replace(/\D/g, "");
          const lines = list
            .slice(0, 30)
            .map((r, i) => `${i + 1}. LR ${String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "")} — ${r.customer_name || r.consignor || "—"} (${r.origin || "—"} → ${r.destination || "—"})${r.invoice_no ? ` | Inv ${r.invoice_no}` : ""}`);
          const msg = `*NTC — POD PENDING (${b.toUpperCase()} BRANCH)*\n\n${lines.join("\n")}${list.length > 30 ? `\n…and ${list.length - 30} more` : ""}\n\n*Total: ${list.length} consignment(s) without POD.* Please upload the signed PODs today.`;
          if (!digits) toast.info(`No WhatsApp number for ${b} — set it in Settings → Branch WhatsApp Contacts. Opening WhatsApp without a number.`);
          window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
        };
        return (
          <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="pod-branch-pills">
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">POD pending by branch:</span>
            {names.map((b) => {
              const active = branchPick === b;
              return (
                <span key={b} className="inline-flex items-center overflow-hidden rounded-full bg-amber-100">
                  <button
                    type="button"
                    className={`px-2.5 py-1 text-[11px] font-bold text-amber-800 transition-transform hover:scale-105 ${active ? "ring-2 ring-inset ring-slate-700" : ""}`}
                    title={active ? "Tap again to show all branches" : `Show only ${b} consignments`}
                    onClick={() => setBranchPick(active ? "All" : b)}
                    data-testid={`pod-pill-${b.toLowerCase()}`}
                  >
                    {b}: {byBranch[b].length} pending
                  </button>
                  <button
                    type="button"
                    className="border-l border-amber-200 px-2 py-1 text-emerald-700 hover:bg-emerald-50"
                    title={`WhatsApp ${b}'s missing-POD list to the branch`}
                    onClick={() => chaseBranch(b)}
                    data-testid={`pod-pill-wa-${b.toLowerCase()}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                  </button>
                </span>
              );
            })}
            {(() => {
              const chaseable = names.filter((b) => b !== "—");
              if (chaseable.length < 2) return null;
              const idx = chaseAllIdx % chaseable.length;
              return (
                <button
                  type="button"
                  className="ml-1 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold text-white transition-transform hover:scale-105 hover:bg-emerald-700"
                  title={`Fires each branch's missing-POD chase in sequence — next: ${chaseable[idx]}`}
                  data-testid="pod-chase-all-btn"
                  onClick={() => {
                    chaseBranch(chaseable[idx]);
                    const next = idx + 1;
                    if (next >= chaseable.length) {
                      setChaseAllIdx(0);
                      toast.success(`All ${chaseable.length} branches chased`);
                    } else {
                      setChaseAllIdx(next);
                      toast.info(`Sent ${chaseable[idx]} — tap again for ${chaseable[next]} (${next + 1}/${chaseable.length})`);
                    }
                  }}
                >
                  Chase All ({idx + 1}/{chaseable.length})
                </button>
              );
            })()}
          </div>
        );
      })()}

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="pod-select-all" aria-label="Select all" />
              </TableHead>
              <TableHead className="w-10 text-[11px] uppercase tracking-[0.12em] text-slate-500">SL No.</TableHead>
              {["Branch", "LR No.", "LR Date", "From", "Destination", "Consignor Name", "Customer Code", "Consignee Name", "PKG", "Basis", "Invoice No.", "Invoice Date", "Invoice Value", "Vehicle No.", "Vehicle Type", "Freight Amount", "Vendor/Broker Name", "Delivery Date", "POD"].map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  {h}
                </TableHead>
              ))}
              <TableHead className="w-[170px] text-right text-[11px] uppercase tracking-[0.12em] text-slate-500">
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 21 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={21} className="py-12 text-center text-muted-foreground">
                  No consignments{fromDate || toDate ? " in the selected date range" : " available"}.
                </TableCell>
              </TableRow>
            )}
            {rows &&
              pagedPods.map((r, i) => (
                <TableRow key={r.id} data-testid="pod-row" className="whitespace-nowrap" data-state={selected.has(r.id) ? "selected" : undefined}>
                  <TableCell className="w-10">
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid="pod-select-row" aria-label="Select row" />
                  </TableCell>
                  <TableCell className="text-sm">{podOffset + i + 1}</TableCell>
                  <TableCell className="text-sm" data-testid="pod-branch-cell">{r.office || r.office_name || "—"}</TableCell>
                  <TableCell className="text-sm font-medium">{String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.lr_date || r.dispatch_date)}</TableCell>
                  <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                  <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {r.consignor || "—"}
                    {(() => {
                      const nm = String(r.consignor || r.customer_name || "").trim().toLowerCase();
                      const p = nm ? portals.find((x) => String(x.customer_name).trim().toLowerCase() === nm) : null;
                      return p ? (
                        <span className="ml-1.5 inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[9px] font-bold text-sky-700" title={`Portal: ${p.visits || 0} visit(s) · ${p.pod_count || 0} POD(s) online`} data-testid="pod-portal-count-badge">
                          {p.visits || 0}v · {p.pod_count || 0} POD
                        </span>
                      ) : null;
                    })()}
                  </TableCell>
                  <TableCell className="text-sm font-medium" data-testid="pod-cust-code">{custCode(r) || "—"}</TableCell>
                  <TableCell className="text-sm">{r.consignee || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="pod-pkg-cell">{r.packages || "—"}</TableCell>
                  <TableCell className="text-sm">{r.basis || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="pod-invoice-no">{r.invoice_no || "—"}</TableCell>
                  <TableCell className="text-sm">{r.invoice_date ? fmtDate(r.invoice_date) : "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</TableCell>
                  <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="pod-vehicle-type">{r.vehicle_type || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
                  <TableCell className="text-sm">{r.vendor_name || "—"}</TableCell>
                  <TableCell className="text-sm">{r.delivery_date ? fmtDate(r.delivery_date) : "—"}</TableCell>
                  <TableCell>
                    {r.pod_path ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                        <FileCheck2 className="h-3.5 w-3.5" /> Uploaded
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Pending</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <input
                      ref={(el) => (fileInputs.current[r.id] = el)}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      className="hidden"
                      onChange={(e) => onFile(r.id, e)}
                      data-testid={`pod-file-input-${r.id}`}
                    />
                    <input
                      ref={(el) => (cameraInputs.current[r.id] = el)}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => onFile(r.id, e)}
                      data-testid={`pod-camera-input-${r.id}`}
                    />
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1 border-emerald-300 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                        title="Photograph the signed consignee copy — uploads as POD in one step"
                        disabled={uploadingId === r.id}
                        onClick={() => cameraInputs.current[r.id]?.click()}
                        data-testid="scan-pod-btn"
                      >
                        <Camera className="h-3.5 w-3.5" /> Scan
                      </Button>
                      {r.pod_path && (
                        <a href={podUrl(r)} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" data-testid="view-pod-btn">
                            <ExternalLink className="h-3.5 w-3.5" /> View
                          </Button>
                        </a>
                      )}
                      {r.pod_path && (
                        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-red-600" title="Remove uploaded POD" onClick={() => deletePod(r)} data-testid="delete-pod-btn">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={r.pod_path ? "outline" : "default"}
                        className="h-8 gap-1 text-xs"
                        disabled={uploadingId === r.id}
                        onClick={() => pick(r.id)}
                        data-testid="upload-pod-btn"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {uploadingId === r.id ? "Uploading…" : r.pod_path ? "Replace" : "Upload POD"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>
      <ListPagination total={filtered.length} page={page} setPage={setPage} noun="consignments" testPrefix="pod" />

      <Dialog open={portalOpen} onOpenChange={(o) => { setPortalOpen(o); if (!o) { setPortalLink(""); setPortalCust(""); setPortalMeta(null); } }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="pod-portal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Customer POD Portal</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">One permanent link per customer showing ALL their delivered PODs — share it once instead of per-LR links. It updates automatically as new PODs upload.</p>
          <div className="grid gap-1.5">
            <Label>Customer</Label>
            <select
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
              value={portalCust}
              onChange={(e) => { setPortalCust(e.target.value); setPortalLink(""); setPortalMeta(null); }}
              data-testid="pod-portal-customer"
            >
              <option value="">Select customer…</option>
              {[...customersList].sort((a, b) => String(a.name).localeCompare(String(b.name))).map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          {!portalLink ? (
            <Button
              disabled={!portalCust}
              onClick={() => {
                api.post("/pod-portal", { customer_name: portalCust })
                  .then((r) => { setPortalLink(`${API}/public/pod-portal/${r.data.id}`); setPortalMeta(r.data); loadPortals(); })
                  .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
              }}
              data-testid="pod-portal-create-btn"
            >
              Get Portal Link
            </Button>
          ) : (
            <div className="grid gap-2">
              <div className="break-all rounded-md border bg-slate-50 px-3 py-2 text-xs" data-testid="pod-portal-link">{portalLink}</div>
              <div className={`rounded-md px-3 py-1.5 text-xs font-medium ${portalMeta?.last_visited_at ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="pod-portal-visit-info">
                {portalMeta?.last_visited_at
                  ? `Customer last opened: ${new Date(portalMeta.last_visited_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} · ${portalMeta.visits || 0} visit(s)`
                  : "Not opened by customer yet"}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { navigator.clipboard?.writeText(portalLink); toast.success("Link copied"); }}
                  data-testid="pod-portal-copy-btn"
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() => {
                    const cust = customersList.find((c) => c.name === portalCust);
                    const digits = String(cust?.phone || "").replace(/\D/g, "");
                    const msg = [
                      "*NTC — Your POD Portal*",
                      `Dear ${portalCust},`,
                      "View ALL your delivered consignments and signed PODs anytime at:",
                      portalLink,
                      "",
                      "The page updates automatically as new deliveries complete.",
                      "— Nisha Transport Corporation",
                    ].join("\n");
                    window.open(`https://wa.me/${digits ? (digits.length === 10 ? "91" + digits : digits) : ""}?text=${encodeURIComponent(msg)}`, "_blank");
                  }}
                  data-testid="pod-portal-wa-btn"
                >
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp Customer
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(portalLink, "_blank")} data-testid="pod-portal-open-btn">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
